/**
 * Component: SpeechToTextComponent
 *
 * Description:
 * This component enables speech-to-text functionality using Microsoft Cognitive Services Speech SDK.
 * It provides a toggle button to start/stop listening, displays a tooltip for user guidance, and
 * sends recognized speech data to a parent component via callback props.
 */

import React, { useState, useEffect, useMemo, useRef } from "react";
import { useSelector } from "react-redux";
import * as sdk from "microsoft-cognitiveservices-speech-sdk";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faMicrophone, faStop } from "@fortawesome/free-solid-svg-icons";

import "./SpeechToTextComponent.css"; // Styling for round-button and tooltip

// Custom hook to retrieve session ID from sessionStorage
const useSession = () => {
  const [sessionId, setSessionId] = useState(
    sessionStorage.getItem("sessionId") || null
  );
  return sessionId;
};

const SpeechToTextComponent = ({
  onTranscriptChange, // Callback to send final transcript
  onData, // Callback to indicate listening state
  disableItem, // Boolean to disable the mic button externally
}) => {
  const accessToken = useSelector((state) => state.auth.accessToken); // Access token from Redux store
  const sessionId = useSession(); // Get session ID from sessionStorage

  const [isListening, setIsListening] = useState(false); // Track if mic is active
  const [recognizer, setRecognizer] = useState(null); // SDK recognizer instance
  const [myTranscript, setMyTranscript] = useState(""); // Final recognized result
  const [recognizingTranscript, setRecognizingTranscript] = useState(""); // In-progress result
  const [speechToken, setSpeechToken] = useState(null); // Fetched Speech Token
  const [speechRegion, setSpeechRegion] = useState(null); // Fetched Speech Region

  const refreshTokenInterval = useRef(null); // Store token refresh interval

  // Fetch Speech Token and Region from API
  const fetchSpeechToken = async () => {
    try {
      const endpoint = process.env.REACT_APP_AVATAR_API_GET_SPEECH_TOKEN_ENDPOINT;

      const options = {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${accessToken}`,
        },
        body: JSON.stringify({
          unique_id: sessionId,
        }),
      };

      const response = await fetch(endpoint, options);
      const data = await response.json();

      const { token, region } = data;
      setSpeechToken(token);
      setSpeechRegion(region);
    } catch (error) {
      console.error("Error fetching speech token:", error);
    }
  };

  // Initial fetch
  useEffect(() => {
    if (accessToken && sessionId) {
      fetchSpeechToken();
    }
  }, [accessToken, sessionId]);

  // Refresh token every 9 minutes (token is valid for 10)
  useEffect(() => {
    if (speechToken && speechRegion && accessToken && sessionId) {
      refreshTokenInterval.current = setInterval(() => {
        fetchSpeechToken();
      }, 9 * 60 * 1000); // 9 minutes

      return () => {
        clearInterval(refreshTokenInterval.current);
      };
    }
  }, [speechToken, speechRegion, accessToken, sessionId]);

  // Effect: Starts/stops recognition based on `isListening` state
  useEffect(() => {
    if (isListening && speechToken && speechRegion) {
      const speechConfig = sdk.SpeechConfig.fromAuthorizationToken(
        speechToken,
        speechRegion
      );
      speechConfig.speechRecognitionLanguage = "en-US";
      const audioConfig = sdk.AudioConfig.fromDefaultMicrophoneInput();

      // Create recognizer instance
      const newRecognizer = new sdk.SpeechRecognizer(speechConfig, audioConfig);

      // Handle final result
      const processRecognizedTranscript = (event) => {
        const result = event.result;
        if (result.reason === sdk.ResultReason.RecognizedSpeech) {
          const transcript = result.text;
          setMyTranscript(transcript);
          onTranscriptChange(transcript); // Pass result to parent
        }
      };

      // Handle intermediate (live) results
      const processRecognizingTranscript = (event) => {
        const result = event.result;
        if (result.reason === sdk.ResultReason.RecognizingSpeech) {
          setRecognizingTranscript(result.text);
        }
      };

      // Bind events to recognizer
      newRecognizer.recognized = (s, e) => processRecognizedTranscript(e);
      newRecognizer.recognizing = (s, e) => processRecognizingTranscript(e);

      // Start recognition
      newRecognizer.startContinuousRecognitionAsync();

      setRecognizer(newRecognizer);
    } else {
      // Stop recognition and release recognizer instance
      if (recognizer) {
        recognizer.stopContinuousRecognitionAsync();
        setRecognizer(null);
      }
    }

    // Cleanup on unmount or stop
    return () => {
      if (recognizer) {
        recognizer.stopContinuousRecognitionAsync();
      }
    };
  }, [isListening, speechToken, speechRegion, onTranscriptChange]);

  // Toggle mic on/off
  const toggleListening = () => {
    setIsListening(!isListening);
    onData(!isListening); // Inform parent of updated state
  };

  // External control to stop mic when disabled
  useMemo(() => {
    if (disableItem) {
      setIsListening(false);
    }
  }, [disableItem]);

  return (
    <div>
      <button
        className={`round-button ${isListening ? "stop" : "start"}`}
        style={{ background: disableItem ? "#747480" : undefined }}
        disabled={disableItem || !speechToken || !speechRegion}
        onClick={toggleListening}
      >
        {/* Tooltip text for better UX */}
        <span className="tooltip">
          {isListening ? "Stop Listening" : "Start Listening"}
        </span>

        {/* Icon changes based on state */}
        <FontAwesomeIcon icon={isListening ? faStop : faMicrophone} />
      </button>
    </div>
  );
};

export default SpeechToTextComponent;
